/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { isModel, TGenericModelNode } from "@adapptio/lib-blueprint";
import { mapModelToNodes } from "./mapModelToNodes";
import { InspectorNode } from "./node/InspectorNode";
import { createDefaultNode } from "./createDefaultNode";
import { useInspectorNode } from "../ui/hooks/useInspectorNode";

export interface IInspectorProps {
	children?: React.ReactNode;
	model?: TGenericModelNode;
	label?: string;
	depth?: number;
	group?: boolean;
}

function arePropsEqual(prevProps: IInspectorProps, nextProps: IInspectorProps): boolean {
	if (prevProps.label !== nextProps.label) {
		return false;
	}

	if (prevProps.group !== nextProps.group) {
		return false;
	}

	if (prevProps.model?.nodeId !== nextProps.model?.nodeId) {
		return false;
	}

	if (!prevProps.model && !nextProps.model) {
		return false;
	}

	return true;
}

const Inspector: React.FC<IInspectorProps> = ({ model, label, children, depth, group }) => {
	const modelNode = isModel(model) ? mapModelToNodes(model) : createDefaultNode();

	useInspectorNode(modelNode);

	const inspectorNode = {
		...modelNode,
		group,
		label: label || modelNode.label
	};

	return (
		<InspectorNode node={inspectorNode} depth={depth}>
			{children}
		</InspectorNode>
	);
};

const InspectorMemo = React.memo(Inspector, arePropsEqual);

export { InspectorMemo as Inspector };
